import {GameConfig, SideReelFeaturesList} from "../GameConfig";
import {GameSymbol, LowSymbolsList, MidSymbolsList, SymbolsList, SymbolStates} from "./GameSymbol";
import {ISymbol, ISymbolPosition} from "../../core/reel/IReelData";
import {gsap} from "gsap";
import {HOTB} from "../HOTB";
import {ExtendedGameModel} from "../server/ExtendedGameModel";
import {HOTBData, HOTBGameData} from "../server/HOTBData";
import {GameUtils} from "../utils/GameUtils";
import {CustomBounce} from "gsap/dist/CustomBounce";
import {GameSounds} from "../sound/GameSounds";
import {IBetWayWinData} from "@nolimitcity/slot-game/bin/utils/server/IData";
import {ExtendedGameMode} from "../server/ExtendedGameMode";
import {AnimUtils} from "../utils/AnimUtils";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";

export class ReelView extends PIXI.Container {
    private readonly _reelId: number;
    private _allOutSymbols: ISymbol[] = [];
    private _allInSymbols: ISymbol[] = [];
    private _collapsedReel: ISymbol[] = [];

    constructor(id: number) {
        super();
        this.name = "Reel " + id;
        this._reelId = id;
        this.position.set(id * GameConfig.symbolWidth, 0);
    }

    private _blinkSoundPlayed: boolean = false;

    get blinkSoundPlayed(): boolean {
        return this._blinkSoundPlayed;
    }

    set blinkSoundPlayed(value: boolean) {
        this._blinkSoundPlayed = value;
    }

    private _hasAnticipation: boolean = false;

    get hasAnticipation(): boolean {
        return this._hasAnticipation;
    }

    set hasAnticipation(value: boolean) {
        this._hasAnticipation = value;
    }

    get needCollapse(): boolean {
        return (this._collapsedReel.length != this._allOutSymbols.length);
    }

    get needFillUp(): boolean {
        return (this._allOutSymbols.length < GameConfig.totalReels);
    }

    public getSymbol(position: number): any {
        for (let i: number = 0; i < this.children.length; i++) {
            if ((<GameSymbol>this.children[i]).reelTargetPosition === position) {
                return this.children[i];
            }
        }

        // fallbacks
        for (let i: number = 0; i < this._allOutSymbols.length; i++) {
            if (this._allOutSymbols[i].reelPosition === position) {
                return this._allOutSymbols[i];
            }
        }

        if (position > (this._allOutSymbols.length - 1)) {
            position = (this._allOutSymbols.length - 1);
        }
        return this._allOutSymbols[position];
    }

    public reInitReel(symbols: string[]): void {
        const gameModel: ExtendedGameModel = HOTB.model,
            hotbData: HOTBData = gameModel.data;

        for (let i: number = 0; i < symbols.length; i++) {
            if (symbols[i] !== SymbolsList.BLANK) {
                let symbol: ISymbol;

                if (hotbData.nudgeMultipliersCurrent[this._reelId] && GameUtils.hasSameValues(symbols, SymbolsList.XNUDGE)) {
                    symbol = this.createSymbol(symbols[i], SymbolStates.NORMAL, {
                        reelId: this._reelId,
                        startPosition: (i),
                        targetPosition: (i)
                    }, true);

                    // show only last nudge sym
                    if (i === 3) {
                        const splitCount: number = hotbData.nudgeMultipliersCurrent[this._reelId] / hotbData.nudgeMultipliersBeforeSplit[this._reelId];
                        (<GameSymbol>symbol).visible = true;
                        (<GameSymbol>symbol).showNudgeMulti();
                        (<GameSymbol>symbol).splitCount = Math.min(splitCount, GameConfig.MAX_PRESENTABLE_SPLITS);
                        (<GameSymbol>symbol).getStateAnimation(SymbolStates.NORMAL);
                    } else {
                        (<GameSymbol>symbol).visible = false;
                    }
                } else {
                    let symName: string = symbols[i];

                    symbol = this.createSymbol(
                        (symName === SymbolsList.LAUNCH_WILD) ? SymbolsList.WILD : symName,
                        SymbolStates.NORMAL, {
                            reelId: this._reelId,
                            startPosition: (i),
                            targetPosition: (i)
                        });

                    if (symbols[i] === SymbolsList.LAUNCH_WILD) {
                        (<GameSymbol>symbol).updateSymbolMultiplier(hotbData.symbolSizes[this._reelId][i]);
                        (<GameSymbol>symbol).splitCount = hotbData.symbolSizes[this._reelId][i] / hotbData.symbolMultipliers[this._reelId][i];
                    } else {
                        (<GameSymbol>symbol).splitCount = hotbData.symbolSizes[this._reelId][i];
                    }
                    if ((<GameSymbol>symbol).splitCount > 1) {
                        (<GameSymbol>symbol).getStateAnimation(SymbolStates.NORMAL);
                    }
                }

                this._allOutSymbols.push(symbol);
            }
        }
    }

    public initReel(symbols: string[]): void {
        const gameModel: ExtendedGameModel = HOTB.model,
            isRestore: boolean = gameModel.isRestore,
            hotbData: HOTBData = gameModel.data;

        for (let i: number = 0; i < symbols.length; i++) {
            if (isRestore) {
                if (symbols[i] !== SymbolsList.BLANK) {
                    let symbol: ISymbol;

                    if (hotbData.nudgeMultipliers[this._reelId] && GameUtils.hasSameValues(symbols, SymbolsList.XNUDGE)) {
                        symbol = this.createSymbol(symbols[i], SymbolStates.NORMAL, {
                            reelId: this._reelId,
                            startPosition: (i),
                            targetPosition: (i)
                        }, true);

                        // show only last nudge sym
                        if (i === 3) {
                            const splitCount: number = hotbData.nudgeMultipliers[this._reelId] / hotbData.nudgeMultiplierBeforeSplit[this._reelId];
                            (<GameSymbol>symbol).visible = true;
                            (<GameSymbol>symbol).showNudgeMulti();
                            (<GameSymbol>symbol).splitCount = Math.min(splitCount, GameConfig.MAX_PRESENTABLE_SPLITS);
                            (<GameSymbol>symbol).getStateAnimation(SymbolStates.NORMAL);
                        } else {
                            (<GameSymbol>symbol).visible = false;
                        }
                    } else {
                        let symName: string = symbols[i];

                        symbol = this.createSymbol(
                            (symName === SymbolsList.LAUNCH_WILD) ? SymbolsList.WILD : symName,
                            SymbolStates.NORMAL, {
                                reelId: this._reelId,
                                startPosition: (i),
                                targetPosition: (i)
                            });

                        if (symbols[i] === SymbolsList.LAUNCH_WILD) {
                            (<GameSymbol>symbol).updateSymbolMultiplier(gameModel.updatedSymbolSizes[this._reelId][i]);
                            (<GameSymbol>symbol).splitCount = gameModel.updatedSymbolSizes[this._reelId][i] / hotbData.launchMultiplierBeforeSplit[this._reelId][i];
                        } else {
                            (<GameSymbol>symbol).splitCount = gameModel.updatedSymbolSizes[this._reelId][i];
                        }

                        if ((<GameSymbol>symbol).splitCount > 1) {
                            (<GameSymbol>symbol).getStateAnimation(SymbolStates.NORMAL);
                        }
                    }

                    if (hotbData.nextMode === ExtendedGameMode.CAPWIN_GAME ||
                        hotbData.nextMode === ExtendedGameMode.FREESPIN ||
                        hotbData.nextMode === ExtendedGameMode.FREESPIN_SUPER) {
                        this._allOutSymbols.push(symbol);
                    } else {
                        this._collapsedReel.push(symbol);
                    }
                }
            } else {
                const symbol: ISymbol = this.createSymbol(symbols[i], SymbolStates.NORMAL, {
                    reelId: this._reelId,
                    startPosition: (i),
                    targetPosition: (i)
                });
                this._allOutSymbols.push(symbol);
            }
        }
    }

    public createSymbol(symbolName: string, state: SymbolStates, symbolPositionData: ISymbolPosition, isNudgeSym: boolean = false): ISymbol {
        const symbol: GameSymbol = new GameSymbol(symbolName, state, symbolPositionData, isNudgeSym),
            offset: number = symbolPositionData.offset ? symbolPositionData.offset : 0;
        symbol.position.set(0, symbolPositionData.startPosition * GameConfig.symbolHeight - offset);
        symbol.reelTargetPosition = symbolPositionData.targetPosition;
        symbol.reelPosition = symbolPositionData.startPosition;
        this.addChild(symbol);
        return symbol;
    }

    public collapseReel(reelDelay: number): gsap.core.Timeline {
        this._collapsedReel = [];
        for (let i: number = this._allOutSymbols.length - 1; i > -1; i--) {
            if (this._allOutSymbols[i].isRemoved) {
                // If i = 0 it's the top symbol which can't fall down
                if (i > 0) {
                    for (let j: number = i; j > 0; j--) {
                        if (!this._allOutSymbols[j - 1].isRemoved) {
                            this._allOutSymbols[j - 1].reelTargetPosition++;
                        }
                    }
                }
            } else {
                this._collapsedReel.push(this._allOutSymbols[i]);
            }
        }
        return this.getCollapseAnimation(reelDelay);
    }

    public getAllOutAnimation(): gsap.core.Timeline {
        const returnTimeline: gsap.core.Timeline = gsap.timeline();
        let timeline: gsap.core.Timeline;
        const subTimelines: gsap.core.Timeline[] = [];
        let counter: number = 0;
        while (this._allOutSymbols.length > 0) {
            const symbol: GameSymbol = <GameSymbol>this._allOutSymbols.pop()!;
            symbol.reelTargetPosition = GameConfig.totalSymsPerReel;
            if (symbol === null) {
                throw new Error(`AvalancheReelPartAnimator.animateAllOut, symbol is null!`);
            } else {
                timeline = gsap.timeline();
                timeline.add([
                    gsap.timeline({
                        delay: GameUtils.isFastSpin() ? GameConfig.mainReelAllOutBlurDelayFastSpin : GameConfig.mainReelAllOutBlurDelay,
                    }).add((<GameSymbol>symbol).getStateAnimation(SymbolStates.NORMAL)),
                    gsap.to(symbol.position, {
                        duration: GameUtils.isFastSpin() ? GameConfig.reelAllOutDurationFastSpin : GameConfig.reelAllOutDuration,
                        y: (GameConfig.symbolHeight * symbol.reelTargetPosition),
                        ease: "power3.in",
                        delay: GameConfig.reelSpinStartSymbolDelay * counter
                    })
                ]);
                timeline.add((): void => {
                    this.removeSymbol(symbol);
                });
                subTimelines.push(timeline);
            }
            counter++;
        }
        returnTimeline.add(subTimelines);
        return returnTimeline;
    }

    public removeSymbol(symbol: ISymbol): void {
        if (symbol) {
            (<GameSymbol>symbol).resetBobble();
            AnimUtils.cleanCareFully(<GameSymbol>symbol);
            this.removeChild(<GameSymbol>symbol);
        }
    }

    public stop(reelData: string[]): gsap.core.Timeline {
        for (let i: number = 0; i < GameConfig.totalSymsPerReel; i++) {
            const symbol: ISymbol = this.createSymbol(reelData[i], SymbolStates.NORMAL, {
                reelId: this._reelId,
                startPosition: -((GameConfig.totalSymsPerReel - i) + 1),
                targetPosition: i
            });
            this._allInSymbols.push(symbol);
        }
        const tl: gsap.core.Timeline = gsap.timeline();
        tl.add(this.getAllInAnimation());
        return tl;
    }

    public getAllInAnimation(): gsap.core.Timeline {
        const returnTimeline: gsap.core.Timeline = gsap.timeline(),
            subTimelines: gsap.core.Timeline[] = [];

        let counter: number = 0;

        this._allOutSymbols = [];

        while (this._allInSymbols.length > 0) {
            const symbol: ISymbol = this._allInSymbols.pop()!;

            this._allOutSymbols.unshift(symbol);

            if (symbol === null) {
                throw new Error(`AvalancheReelPartAnimator.animateAllIn, symbol is null!`);
            } else {
                const symbolTL: gsap.core.Timeline = gsap.timeline(),
                    fastSpin: boolean = GameUtils.isFastSpin(),
                    totalSpinDuration: number = fastSpin ? GameConfig.mainAllInTotalSpinDurationFastSpin : GameConfig.mainAllInTotalSpinDuration,
                    symSpinStopDuration: number = (fastSpin ? GameConfig.mainReelSpinStopSymbolDelayFastSpin : GameConfig.mainReelSpinStopSymbolDelay) + (counter * 0.01);

                symbolTL.to(symbol.position, {
                    duration: totalSpinDuration,
                    delay: symSpinStopDuration + GameConfig.NEAR_SCATTER_WIN_DELAY,
                    y: GameConfig.symbolHeight * symbol.reelTargetPosition,
                    ease: CustomBounce.create("myBounce", {
                        strength: 0.2,
                        endAtStart: false,
                        squash: 1,
                        squashID: "myBounce-squash"
                    })
                });

                symbolTL.add([
                    (<GameSymbol>symbol).getStateAnimation(SymbolStates.LANDING, (this._allInSymbols.length === 0 && this._reelId === 4)),
                    (): void => {
                        this.onSymLandingPlaySound(symbol, false);
                    }
                ]);

                symbolTL.add((): void => {
                    this.checkForPossibleWin(symbol, false);
                    symbol.reelPosition = symbol.reelTargetPosition;
                });

                subTimelines.push(symbolTL);
            }
            counter++;
        }

        returnTimeline.add((): void => {
            GameConfig.NEAR_SCATTER_WIN_DELAY = 0;
            if (!(HOTB.model.gameData.nextMode.includes("AVALANCHE"))) {
                GameConfig.scatterLandedInReels = [false, false, false, false, false];
            }
        });
        returnTimeline.add(subTimelines);
        return returnTimeline;
    }

    public getFillUpReelAnimation(delay: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        let reelLength: number = HOTB.model.reels[this._reelId].length;
        const numberOfFillUpSymbols: number = reelLength - this._collapsedReel.length;
        for (let i: number = numberOfFillUpSymbols - 1; i >= 0; i--) {
            const symbol: ISymbol = this.createSymbol(HOTB.model.gameData.reels[this._reelId][i], SymbolStates.NORMAL, {
                reelId: this._reelId,
                startPosition: (0 - ((numberOfFillUpSymbols - i))),
                targetPosition: i,
                offset: 20
            });
            symbol.reelPosition = -2;
            this._collapsedReel.unshift(symbol);
        }
        tl.add(this.getFillUpAnimation(), delay);
        return tl;
    }

    public replaceSymbol(reelId: number, symId: number, convertTo: string): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        if (reelId === this._reelId) {
            const existingSymbol: ISymbol = this.getSymbol(symId),
                replaceIndex: number = this._allOutSymbols.indexOf(existingSymbol),
                isBobbling: boolean = (existingSymbol as GameSymbol).isBobbling;

            tl.add((): void => {
                this._allOutSymbols[replaceIndex] = this.createSymbol(convertTo, SymbolStates.NORMAL, {
                    reelId: reelId,
                    startPosition: symId,
                    targetPosition: symId
                });

                if (isBobbling) {
                    (this._allOutSymbols[replaceIndex] as GameSymbol).startBobbleShake();
                }
            });

            // #312 delay used to avoid jerk issue
            tl.to(this, {
                duration: 0.05,
                onComplete: (): void => {
                    this.removeSymbol(existingSymbol);
                }
            });
        }

        return tl;
    }

    public switchXBombForMaxWin(reelId: number, symId: number): void {
        if (reelId === this._reelId) {
            for (let i: number = 0; i < this._allOutSymbols.length; i++) {
                const currOutSymbol: ISymbol = this._allOutSymbols[i];

                if (currOutSymbol.reelTargetPosition === symId) {
                    for (let j: number = 0; j < this._collapsedReel.length; j++) {
                        const currCollapsedSym: ISymbol = this._collapsedReel[j];

                        if (currCollapsedSym.reelTargetPosition === symId) {
                            this._allOutSymbols[i] = currCollapsedSym;

                            // remove the symbol from collapsedReel
                            this._collapsedReel.splice(j, 1);
                            break;
                        }
                    }
                }
            }
        }
    }

    public transformSymbol(reelId: number, symId: number, convertTo: string): void {
        let desiredSymState: SymbolStates = SymbolStates.NORMAL;

        if (convertTo === SymbolsList.LAUNCH) {
            desiredSymState = SymbolStates.IDLE;
        }

        if (convertTo === SymbolsList.MAGA_CAP) {
            desiredSymState = SymbolStates.LANDING;
        }

        const symbol: ISymbol = this.createSymbol(convertTo, desiredSymState, {
            reelId: reelId,
            startPosition: symId,
            targetPosition: symId
        });

        this._collapsedReel.push(symbol);

        if (convertTo === SymbolsList.WILD ||
            convertTo === SymbolsList.XBOMB ||
            convertTo === SymbolsList.LAUNCH) {
            this.checkForPossibleWinFromFeatures(reelId, symId, convertTo);
        }
    }

    public createNudgeWild(reelId: number, symId: number, showSym: boolean = true): void {
        const symbol: ISymbol = this.createSymbol(SymbolsList.XNUDGE, SymbolStates.NORMAL, {
            reelId: reelId,
            startPosition: symId,
            targetPosition: symId
        }, true);

        (<GameSymbol>symbol).visible = showSym;
        (<GameSymbol>symbol).showNudgeMulti();

        this._collapsedReel.push(symbol);
    }

    public removeNudgedSymbol(): void {
        if (this._collapsedReel.length) {
            const nudgedSym: ISymbol = this._collapsedReel.reduce((minItem: ISymbol, currentItem: ISymbol): ISymbol => {
                return currentItem.reelPosition < minItem.reelPosition ? currentItem : minItem;
            });

            nudgedSym.isRemoved = true;
            this.removeSymbol(nudgedSym);
            this._collapsedReel.splice(this._collapsedReel.indexOf(nudgedSym), 1);
        }
    }

    public splitNudgeSymbols(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        for (let i: number = 0; i < this._allOutSymbols.length; i++) {
            const currSymbol: ISymbol = this._allOutSymbols[i],
                currSplitCount: number = (<GameSymbol>currSymbol).splitCount;

            (<GameSymbol>currSymbol).splitCount = currSplitCount + 2;
            (<GameSymbol>currSymbol).getStateAnimation(SymbolStates.NORMAL);
            (<GameSymbol>currSymbol).updateNudgeMultiplier(HOTB.model.gameData.nudgeMultipliersCurrent[this._reelId]);
        }

        return tl;
    }

    public checkForPossibleWinFromFeatures(reelId: number, symId: number, fromFeature: string): void {
        const gameData: HOTBGameData = HOTB.model.gameData,
            prevLandedSymbols: string[][] = gameData.deleteMap.map((currReel: boolean[], reelIndex: number) => {
                return currReel.map((currSym: boolean, symIndex: number): SymbolsList.BLANK | string =>
                    currSym ? SymbolsList.BLANK : gameData.evaluatedArea[reelIndex][symIndex]
                );
            }),
            sortedLandedSymbols: string[][] = prevLandedSymbols.map((row: string[]) => {
                return [...row].sort((a, b) => (a === "BLANK" ? -1 : 1) - (b === "BLANK" ? -1 : 1));
            });

        let nextPossibleWinningSymbols: string[] = this.getUniqueFilteredSymbols();

        if (fromFeature === SideReelFeaturesList.XBOMB && gameData.betWayWins.length > 0) {
            nextPossibleWinningSymbols = (gameData.betWayWins).map((item: IBetWayWinData) => item.symbol);
        }

        for (let i: number = 0; i < nextPossibleWinningSymbols.length; i++) {
            let arr1: string[][] = gameData.symbolsAfterAvalanche,
                arr2: string[][] = sortedLandedSymbols;

            const nextPossibleWinSym: string = nextPossibleWinningSymbols[i],
                linkedSymPositions: number[][] = this.getLinkedSymPositions(reelId, symId, nextPossibleWinSym, arr1, arr2, fromFeature);

            for (let j: number = 0; j < linkedSymPositions.length; j++) {
                const currLinkedSymPos: number[] = linkedSymPositions[j];
                this.bobbleSymbol(currLinkedSymPos[0], currLinkedSymPos[1]);
            }

            this.playBlinkSound(linkedSymPositions, false, reelId, symId, fromFeature);
        }
    }

    private onSymLandingPlaySound(currLandingSymbol: ISymbol, isOnFillUp: boolean): void {
        const symToCheck: string = SymbolsList.S1,
            currSymbolReelId: number = currLandingSymbol.reelId,
            currSymbolSymId: number = currLandingSymbol.reelTargetPosition;

        if (currLandingSymbol.name === symToCheck) {
            GameConfig.scatterPositions[currSymbolReelId] = currSymbolSymId;
        }

        if (GameConfig.scatterPositions.filter(item => item > -1).length > 1) {
            for (let reelId: number = 0; reelId < GameConfig.scatterPositions.length; reelId++) {
                GameConfig.hasAnticipation[reelId] = GameConfig.scatterPositions[reelId] === -1;
            }
        }

        this.hasAnticipation = GameConfig.hasAnticipation[this._reelId];

        if (this.hasAnticipation && GameConfig.scatterPositions.filter(item => item > -1).length === 4) {
            this.hasAnticipation = false;
        }

        if (this.hasAnticipation) {
            if (currLandingSymbol.reelTargetPosition === 0) {
                HOTB.soundPlayer.playSound(GameSounds.SCATTER_ANTICIPATION_END);
            } else {
                HOTB.soundPlayer.playScatterAnticipationLandingSounds();
            }
        } else {
            let lastSymPos: number = GameConfig.totalSymsPerReel - 1;

            if (isOnFillUp) {
                lastSymPos = HOTB.model.gameData.symbolsAfterAvalanchePrevSpin[this._reelId].lastIndexOf(SymbolsList.BLANK);
            }

            if (currLandingSymbol.reelTargetPosition === lastSymPos) {
                HOTB.soundPlayer.playSound(GameSounds.SYMS_LANDING[this._reelId % 2]);
            }
        }
    }

    private getUniqueFilteredSymbols(): string[] {
        const gameData: HOTBGameData = HOTB.model.gameData,
            symbolsToGetFrom: string[][] = gameData.evaluatedArea,
            validSymbols: (MidSymbolsList | LowSymbolsList | string)[] = [...Object.values(MidSymbolsList), ...Object.values(LowSymbolsList)],
            uniqueSymbols: string[] = [];

        for (let i: number = 0; i < symbolsToGetFrom.length; i++) {
            for (let j: number = 0; j < symbolsToGetFrom[i].length; j++) {
                const symbol: string = symbolsToGetFrom[i][j];
                if (validSymbols.includes(symbol) && !uniqueSymbols.includes(symbol)) {
                    uniqueSymbols.push(symbol);
                }
            }
        }

        return uniqueSymbols;
    }

    private checkForPossibleWin(landingSymbol: ISymbol, isOnFillUp: boolean): void {
        const reelId: number = landingSymbol.reelId,
            gameData: HOTBGameData = HOTB.model.gameData,
            betWayWinsData: IBetWayWinData[] = gameData.betWayWins,
            winningSymbols: string[] = betWayWinsData.map((item: IBetWayWinData) => item.symbol),
            symName: string = landingSymbol.name,
            symId: number = landingSymbol.reelTargetPosition;

        // #419
        if (winningSymbols.includes(symName) || isOnFillUp) {
            const linkedSymPositions: number[][] = this.getLinkedSymPositions(reelId, symId, symName, gameData.evaluatedArea, gameData.symbolsAfterAvalanchePrevSpin);

            for (let i: number = 0; i < linkedSymPositions.length; i++) {
                const currLinkedSymPos: number[] = linkedSymPositions[i];
                this.bobbleSymbol(currLinkedSymPos[0], currLinkedSymPos[1]);
            }

            this.playBlinkSound(linkedSymPositions, isOnFillUp, reelId, symId, symName);

        }
    }

    private playBlinkSound(linkedSymPositions: number[][], isOnFillUp: boolean, reelId: number, symId: number, symName: string): void {
        const gameData: HOTBGameData = HOTB.model.gameData,
            betWayWinsData: IBetWayWinData[] = gameData.betWayWins;

        if (linkedSymPositions.length > 0) {
            if (symName === SymbolsList.WILD ||
                symName === SymbolsList.LAUNCH ||
                symName === SymbolsList.LAUNCH_WILD ||
                symName === SymbolsList.XNUDGE ||
                (symName === SymbolsList.XBOMB)) {
                if (!(this.blinkSoundPlayed)) {
                    HOTB.soundPlayer.playSound(GameSounds.WIN_BLINK);
                    this.blinkSoundPlayed = true;
                }
            } else {
                if (this.hasAnticipation) {
                    HOTB.soundPlayer.playSound(GameSounds.WIN_BLINK);
                } else {
                    if (isOnFillUp) {
                        if (symId === gameData.evaluatedArea[reelId].indexOf(symName) && !(this.blinkSoundPlayed)) {
                            HOTB.soundPlayer.playSound(GameSounds.WIN_BLINK);
                            this.blinkSoundPlayed = true;
                        }
                    } else {
                        if (reelId >= 2 && symId === gameData.evaluatedArea[reelId].indexOf(symName) && symName === betWayWinsData[0].symbol) {
                            HOTB.soundPlayer.playSound(GameSounds.WIN_BLINK);
                        }
                    }
                }
            }
        }
    }

    private bobbleSymbol(reelId: number, symId: number): void {
        const symbol: GameSymbol = (<GameSymbol>HOTB.reelArea.getSymbolAtPosition(reelId, symId));

        if (!symbol.isBobbling) {
            symbol.startBobbleShake();
        }
    }

    private getLandedSymbols(reelId: number, symId: number, arr1: string[][], arr2: string[][]): string[][] {
        const result: string[][] = arr2.map((row: string[]) => [...row]);

        for (let i: number = reelId; i >= 0; i--) {
            for (let j: number = (arr1[i].length - 1); j >= (i < reelId ? 0 : symId); j--) {
                if (result[i][j] !== arr1[i][j]) {
                    result[i][j] = arr1[i][j];
                }
            }
        }

        return result;
    }

    private getLinkedSymPositions(reelId: number, symId: number, symbolName: string, arr1: string[][], arr2: string[][], fromFeature?: string): number[][] {
        let allSymbolsLandedData: string[][] = this.getLandedSymbols(reelId, symId, arr1, arr2);

        const gameData: HOTBGameData = HOTB.model.gameData;

        if (fromFeature === SideReelFeaturesList.XBOMB) {
            allSymbolsLandedData = gameData.evaluatedArea;
        } else {
            const containsBomb: boolean = allSymbolsLandedData.some((row: string[]) => row.includes(SideReelFeaturesList.XBOMB));

            if (containsBomb) {
                allSymbolsLandedData = allSymbolsLandedData.map((row: string[], i: number) =>
                    row.map((item: string, j: number): string => (item === SideReelFeaturesList.XBOMB ? gameData.reels[i][j] : item))
                );
            }
        }

        let connectedResult: number[][] = [],
            found: boolean[] = ArrayHelper.initArrayWithValues(GameConfig.totalReels, () => false);

        for (let i: number = 0; i < allSymbolsLandedData.length; i++) {
            for (let j: number = 0; j < allSymbolsLandedData[i].length; j++) {
                const currSymName: string = allSymbolsLandedData[i][j];

                if (currSymName === symbolName ||
                    currSymName === SymbolsList.WILD ||
                    currSymName === SymbolsList.XNUDGE ||
                    currSymName === SymbolsList.LAUNCH ||
                    currSymName === SymbolsList.LAUNCH_WILD ||
                    // #171
                    // currSymName === SymbolsList.XBOMB ||
                    (fromFeature && fromFeature === SideReelFeaturesList.XBOMB && currSymName === SymbolsList.XBOMB)
                ) {
                    connectedResult.push([i, j]);
                    found[i] = true;
                }
            }
            if (!found[i]) {
                break;
            }
        }

        if (!(found[0] && found[1] && found[2])) {
            connectedResult = [];
        } else {
            if (!found[reelId]) {
                connectedResult = [];
            }
        }

        // not removing this because might need for bug fixing
        /*console.log("--gggg--", reelId, symId, symbolName, connectedResult);
        debugger;*/

        return connectedResult;
    }

    private getCollapseAnimation(reelDelay: number): gsap.core.Timeline {
        const fastSpin: boolean = GameUtils.isFastSpin();
        const collapseTimeline: gsap.core.Timeline = gsap.timeline({delay: fastSpin ? 0 : reelDelay});
        let timeline: gsap.core.Timeline;
        const subTimelines: gsap.core.Timeline[] = [];
        this._allOutSymbols.reverse();
        for (let i: number = 0; i < this._allOutSymbols.length; i++) {
            const symbol: GameSymbol = <GameSymbol>this._allOutSymbols[i];
            if (symbol === null) {
                throw new Error(`AvalancheReelPartAnimator.collapseAnimation, symbol is null!`);
            } else {
                if (symbol.reelTargetPosition > symbol.reelPosition) {
                    timeline = gsap.timeline();
                    const duration: number = (symbol.reelTargetPosition === symbol.position.y) ? 0 : ((fastSpin ? GameConfig.symbolCollapseDurationFastSpin : GameConfig.symbolCollapseDuration));
                    timeline.add([
                        gsap.to(symbol.position, {
                            duration: duration,
                            y: GameConfig.symbolHeight * (symbol.reelTargetPosition),
                            ease: CustomBounce.create("myBounce", {
                                strength: 0.2,
                                endAtStart: false,
                                squash: 1,
                                squashID: "myBounce-squash"
                            })
                        })
                    ]);
                    timeline.add((): void => {
                        symbol.reelPosition = symbol.reelTargetPosition;
                    })
                    subTimelines.push(timeline);
                }
            }
        }
        collapseTimeline.add(subTimelines);
        collapseTimeline.add((): void => {
            if (subTimelines.length) {
                HOTB.soundPlayer.playSound(GameSounds.SYMS_LANDING[HOTB.reelArea.noOfReelsCollapsed % 2]);
                HOTB.reelArea.noOfReelsCollapsed++;
            }
            if (this._reelId === 4) {
                HOTB.reelArea.noOfReelsCollapsed = 0;
            }
        });
        return collapseTimeline;
    }

    private getFillUpAnimation(): gsap.core.Timeline {
        const fillUpTimeline: gsap.core.Timeline = gsap.timeline(),
            subTimelines: gsap.core.Timeline[] = [];

        let counter: number = 0;

        this._allOutSymbols = [];

        while (this._collapsedReel.length > 0) {
            const symbol: ISymbol = this._collapsedReel.pop()!;
            this._allOutSymbols.push(symbol);
            if (symbol === null) {
                throw new Error(`AvalancheReelPartAnimator.animateFillUp, symbol is null!`);
            } else {
                if (symbol.reelTargetPosition > symbol.reelPosition) {
                    const symbolTL: gsap.core.Timeline = gsap.timeline(),
                        fastSpin: boolean = GameUtils.isFastSpin(),
                        totalSpinDuration: number = fastSpin ? GameConfig.fillUpTotalSpinDurationFastSpin : GameConfig.fillUpTotalSpinDuration,
                        symSpinStopDuration: number = (fastSpin ? GameConfig.reelFillUpSpinStopSymbolDelayFastSpin : GameConfig.reelFillUpSpinStopSymbolDelay) + (counter * 0.01);

                    symbolTL.to(symbol.position, {
                        duration: totalSpinDuration,
                        delay: symSpinStopDuration + GameConfig.NEAR_SCATTER_WIN_DELAY,
                        y: GameConfig.symbolHeight * symbol.reelTargetPosition,
                        ease: CustomBounce.create("myBounce", {
                            strength: 0.2,
                            endAtStart: false,
                            squash: 1,
                            squashID: "myBounce-squash"
                        })
                    });

                    symbolTL.add([
                        (<GameSymbol>symbol).getStateAnimation(SymbolStates.LANDING, (HOTB.reelArea.lastReelOfFillUp === this._reelId && this._collapsedReel.length === 0)),
                        (): void => {
                            this.onSymLandingPlaySound(symbol, true);
                            if (symbol.reelTargetPosition === HOTB.model.gameData.symbolsAfterAvalanchePrevSpin[this._reelId].lastIndexOf(SymbolsList.BLANK)) {
                                if (subTimelines.length) {
                                    HOTB.reelArea.noOfReelsCollapsed++;
                                }
                                if (this._reelId === HOTB.reelArea.lastReelOfFillUp) {
                                    HOTB.reelArea.noOfReelsCollapsed = 0;
                                }
                            }
                        }
                    ]);

                    symbolTL.add((): void => {
                        this.checkForPossibleWin(symbol, true);
                        symbol.reelPosition = symbol.reelTargetPosition;
                    });

                    subTimelines.push(symbolTL);
                }
            }
            counter++;
        }
        fillUpTimeline.add((): void => {
            GameConfig.NEAR_SCATTER_WIN_DELAY = 0;
            if (!(HOTB.model.gameData.nextMode.includes("AVALANCHE"))) {
                GameConfig.scatterLandedInReels = [false, false, false, false, false];
            }
        });
        fillUpTimeline.add(subTimelines);
        fillUpTimeline.add((): void => {
            this._allOutSymbols = this.sortSymbolArray(this._allOutSymbols);
        });
        return fillUpTimeline;
    }

    private sortSymbolArray(toBeSorted: ISymbol[]): ISymbol[] {
        const sortedSymbols: ISymbol[] = [];

        // Sorting symbols with reel position 1-X (position 0 is top padding position)
        for (let i: number = 0; i < HOTB.model.evaluatedArea[this._reelId].length; i++) {
            for (let j: number = 0; j < toBeSorted.length; j++) {
                if (toBeSorted[j].reelPosition === i) {
                    sortedSymbols.push(toBeSorted[j]);
                    break;
                }
            }
        }
        return sortedSymbols;
    }
}